import { useEffect, useRef } from 'react';

import { UserProfileResponse } from '@/api/types';
import { ONEID_READY_EVENT_TYPE } from '@/lib/constants';

const sha256Hex = async (input: string): Promise<string> => {
  const bytes = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', bytes);

  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

const attachHem = (email: string) => {
  if (typeof window === 'undefined' || !window.OneIdSDK?.firstParty?.attachHem)
    return;

  const normalized = email.trim().toLowerCase();
  sha256Hex(normalized).then((hem) => {
    try {
      window.OneIdSDK!.firstParty.attachHem(hem, () => undefined);
    } catch (error) {
      console.warn('OneID attachHem error:', error);
    }
  });
};

/**
 * Listens for OneID ready and, when user profile has email, attaches HEM once.
 * Call from app shell where Redux profile is available.
 */
const useOneIdHem = (userProfile: UserProfileResponse | null) => {
  const attachedRef = useRef<string | null>(null);

  useEffect(() => {
    const email = userProfile?.email;

    if (!email || attachedRef.current === email) return () => undefined;

    const doAttach = () => {
      if (attachedRef.current === email) return;

      if (!window.OneIdSDK?.firstParty?.attachHem) return;
      attachedRef.current = email;
      attachHem(email);
    };

    if (window.OneIdSDK?.firstParty?.attachHem) {
      doAttach();

      return () => undefined;
    }

    const onReady = () => doAttach();
    document.addEventListener(ONEID_READY_EVENT_TYPE, onReady);

    return () => document.removeEventListener(ONEID_READY_EVENT_TYPE, onReady);
  }, [userProfile?.email]);
};

export default useOneIdHem;
